import React, {Fragment, useEffect, useState, useRef, useCallback} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {
    Badge,
    Modal,
    ModalBody,
    ModalHeader,
} from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';
import {imgUrl} from '../../../utils/api-request';
import YoutubeTrailer from '../../../components/YoutubeTrailer';
import CrewCard from '../../../components/CrewCard';
import {getLanguageByCode} from '../../../utils/languageCodes';
import '../MoviePage.scss';
import ActionButton from './ActionButton';
import PlayButton from './ActionButton/PlayButton';
import {fetchOrder} from '../../../slices/orders.slice';
import {setCurrentOrder} from '../../../slices/orders.slice';
import {getSitePriceRange, formatCredits} from '../../../utils/credits';
import {fetchMovieVersion, setMovieVersionData} from '../../../slices/movie-versions.slice';
import {fetchOrganizationContent} from '../../../slices/organizations.slice';
import IngestProgress from '../../../components/IngestProgress';
import OrderStatusBadge from '../../../components/OrderStatusBadge';
import PlayingBanner from '../../../components/PlayingBanner';
import HireCountdown from '../../../components/HireCountdown';
import PurchaseTerms from '../../../components/PurchaseTerms';
import PurchaseTimeline from '../../../components/PurchaseTimeline';
import PurchaseHistory from '../../../components/PurchaseHistory';
import { isAdmin } from '../../../utils/admin';
import api from '../../../api';

const defaultCover = 'https://picsum.photos/1480/400';

const SWIPE_THRESHOLD = 0.25; // 25% of viewport width
const SNAP_DURATION = 300; // ms

const MoviePreviewPanel = ({ data, feedItem }) => {
    const movieData = data?.movie || feedItem?.version?.movie;
    const stillUrl = data?.movie?.still?.imageUrl || feedItem?.version?.movie?.still?.imageUrl;
    const posterUrl = data?.movie?.poster?.imageUrl || feedItem?.version?.movie?.poster?.imageUrl;
    const logoUrl = data?.movie?.logo?.imageUrl || feedItem?.version?.movie?.logo?.imageUrl;
    const title = movieData?.title || 'Loading...';
    const bgImage = stillUrl ? imgUrl(stillUrl) : (posterUrl ? imgUrl(posterUrl) : defaultCover);

    return (
        <div className="swipe-preview">
            <div
                className="film-cover d-flex flex-column align-items-end"
                style={{ backgroundImage: `url("${bgImage}")` }}
            >
                <div className="gradient w-100 mt-auto" />
            </div>
            <div className="swipe-preview-info ps-3">
                {logoUrl ? (
                    <img
                        alt={title}
                        src={imgUrl(logoUrl)}
                        className="img-fluid swipe-preview-logo"
                    />
                ) : (
                    <h3 className="py-4 mt-5 text-primary text-center text-uppercase fw-bold">
                        {title}
                    </h3>
                )}
            </div>
        </div>
    );
};

const MoviePageBAC = () => {
    const { movieVersionId, orderId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [modalTrailer, setModalTrailer] = useState(false);
    const { organization, organizationStatus, organizationContent } = useSelector(state => state.organizations);

    const { feed = [], feedIndex = -1 } = location.state || {};
    const hasPrev = feedIndex > 0;
    const hasNext = feedIndex < feed.length - 1;
    const showArrows = feed.length > 1;

    // Swipe refs — no React state during touch = 60fps
    const panelsRef = useRef(null);
    const deltaX = useRef(0);
    const swiping = useRef(false);
    const startX = useRef(0);
    const startY = useRef(0);
    const isHorizontal = useRef(null);
    const animating = useRef(false);
    const dataCache = useRef(new Map());
    const skipNextFetch = useRef(false);


    const { playing } = useSelector(state => state.playMovie);
    const { version, movie, trailers, status: movieVersionStatus } = useSelector(state => state.movieVersions);
    const { currentOrder } = useSelector(state => state.orders);
    const rolesList = ['Actor', 'Director', 'Producer', 'Writer'];
    const user = useSelector(state => state.auth.user);

    const toggleTrailer = () => { setModalTrailer(!modalTrailer); };

    // Pre-fetch adjacent items
    const prefetch = useCallback(async (item) => {
        if (!item || dataCache.current.has(item._id)) return;
        try {
            const [orderRes, versionRes] = await Promise.all([
                api.get(`/orders/${item._id}`),
                api.get(`/movies/versions/${item.version._id}`)
            ]);
            dataCache.current.set(item._id, {
                order: orderRes.data,
                ...versionRes.data
            });

        } catch (e) {
            // Silently fail — prefetch is best-effort
        }
    }, []);

    // Fetch current data
    useEffect(() => {
        if (skipNextFetch.current) {
            skipNextFetch.current = false;
            return;
        }
        if (orderId) {
            dispatch(fetchOrder(orderId));
        } else {
            // No order in the URL (e.g. opened from the homepage highlight) — clear any
            // order left over from a previous view so the page renders without order info.
            dispatch(setCurrentOrder(null));
        }
        dispatch(fetchMovieVersion(movieVersionId));
    }, [dispatch, movieVersionId, orderId]);

    useEffect(() => {
        if (organization?._id) {
            dispatch(fetchOrganizationContent({ orgId: organization._id }));
        }
    }, [dispatch, organization?._id]);

    // Live-poll the order while it is in a transitional post-purchase state,
    // so PLAY MOVIE appears as soon as the backend marks it ready on the mediablock.
    const pollableStatus = currentOrder?.statusWorkflow;
    const pollableOrderId = currentOrder?._id;
    useEffect(() => {
        const transitional = ['ready-to-play', 'movie-delivery-pending', 'key-delivery-pending'];
        if (!pollableOrderId || !transitional.includes(pollableStatus)) return;

        const intervalId = setInterval(() => {
            dispatch(fetchOrder(pollableOrderId));
        }, 5000);
        return () => clearInterval(intervalId);
    }, [dispatch, pollableOrderId, pollableStatus]);

    // Trigger prefetch when current data is ready
    useEffect(() => {
        if (movieVersionStatus !== 'success' || feedIndex < 0) return;
        if (hasPrev) prefetch(feed[feedIndex - 1]);
        if (hasNext) prefetch(feed[feedIndex + 1]);
    }, [movieVersionStatus, feedIndex, feed, hasPrev, hasNext, prefetch]);

    // Commit navigation after swipe
    const commitSwipe = useCallback((direction) => {
        const newIdx = direction === 'next' ? feedIndex + 1 : feedIndex - 1;
        const target = feed[newIdx];
        if (!target) return;

        const cached = dataCache.current.get(target._id);
        if (cached) {
            dispatch(setCurrentOrder(cached.order));
            dispatch(setMovieVersionData({
                version: cached.version,
                movie: cached.movie,
                trailers: cached.trailers || []
            }));
            skipNextFetch.current = true;
        }

        navigate(`/movie-version/${target.version._id}/order/${target._id}`, {
            replace: true,
            state: { feed, feedIndex: newIdx, feedKey: location.state?.feedKey, fromSwipe: true }
        });

        // Reset swipe state
        deltaX.current = 0;
        if (panelsRef.current) {
            panelsRef.current.style.transform = 'translateX(0px)';
        }
    }, [feed, feedIndex, navigate, location.state?.feedKey, dispatch]);

    // Measure actual container width (avoids 100vw mismatch with sidebar)
    const getContainerWidth = useCallback(() => {
        return panelsRef.current?.offsetWidth || window.innerWidth;
    }, []);

    // Animate snap and then commit or bounce back (Web Animations API)
    const snapTo = useCallback((targetPx, onDone) => {
        if (!panelsRef.current) return;
        animating.current = true;

        const current = panelsRef.current.style.transform || 'translateX(0px)';
        const anim = panelsRef.current.animate(
            [{ transform: current }, { transform: `translateX(${targetPx}px)` }],
            { duration: SNAP_DURATION, easing: 'ease-out', fill: 'forwards' }
        );

        anim.onfinish = () => {
            anim.cancel();
            panelsRef.current.style.transform = `translateX(${targetPx}px)`;
            animating.current = false;
            onDone?.();
        };
    }, []);

    // Programmatic swipe for arrow buttons
    const triggerSwipe = useCallback((direction) => {
        if (animating.current) return;
        if (direction === 'next' && !hasNext) return;
        if (direction === 'prev' && !hasPrev) return;

        const w = getContainerWidth();
        const targetPx = direction === 'next' ? -w : w;
        snapTo(targetPx, () => commitSwipe(direction));
    }, [hasNext, hasPrev, snapTo, commitSwipe, getContainerWidth]);

    // Touch handlers
    const onTouchStart = useCallback((e) => {
        if (animating.current) return;
        startX.current = e.touches[0].clientX;
        startY.current = e.touches[0].clientY;
        swiping.current = true;
        isHorizontal.current = null;
        deltaX.current = 0;
    }, []);

    const onTouchMove = useCallback((e) => {
        if (!swiping.current || animating.current) return;

        const dx = e.touches[0].clientX - startX.current;
        const dy = e.touches[0].clientY - startY.current;

        // Determine direction on first significant move
        if (isHorizontal.current === null) {
            if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
            isHorizontal.current = Math.abs(dx) > Math.abs(dy);
            if (!isHorizontal.current) {
                swiping.current = false;
                return;
            }
        }

        // Clamp at boundaries — rubber band effect
        let clampedDx = dx;
        if (dx > 0 && !hasPrev) {
            clampedDx = dx * 0.3; // Rubber band
        } else if (dx < 0 && !hasNext) {
            clampedDx = dx * 0.3; // Rubber band
        }

        deltaX.current = clampedDx;
        if (panelsRef.current) {
            panelsRef.current.style.transform = `translateX(${clampedDx}px)`;
        }
    }, [hasPrev, hasNext]);

    const onTouchEnd = useCallback(() => {
        if (!swiping.current || animating.current) return;
        swiping.current = false;

        const dx = deltaX.current;

        // Tap (not a swipe) — let click events through without triggering snapTo
        if (Math.abs(dx) < 5) return;

        const w = getContainerWidth();
        const threshold = w * SWIPE_THRESHOLD;

        if (dx < -threshold && hasNext) {
            snapTo(-w, () => commitSwipe('next'));
        } else if (dx > threshold && hasPrev) {
            snapTo(w, () => commitSwipe('prev'));
        } else {
            // Snap back
            snapTo(0);
        }
    }, [hasNext, hasPrev, snapTo, commitSwipe, getContainerWidth]);

    if (organizationStatus !== 'success') {
        return null;
    }

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Invalid Date';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';

        const pad = (num) => num.toString().padStart(2, '0');

        return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} at ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const prevItem = hasPrev ? feed[feedIndex - 1] : null;
    const nextItem = hasNext ? feed[feedIndex + 1] : null;

    const fromSwipe = location.state?.fromSwipe;

    const renderMovieDetail = (data, isCurrent) => {
        const { movie: m, version: v, order: o, trailers: t } = data;
        const { statusWorkflow: sw, declineReason: dr } = o || {};
        const suppressAnimation = !isCurrent || fromSwipe;

        // This title can have been booked many times by this cinema — the order
        // document is reused for every one of them. Everything below describes
        // the latest purchase; the earlier ones are listed under Purchase
        // History rather than bleeding into it.
        const purchases = o?.purchases || [];
        const purchase = purchases[purchases.length - 1] || null;
        const screeningDateFmt = formatDateTime(purchase?.screeningDate);

        return (
            <div className={`movie-detail-view bg-black min-vh-100 pb-4${suppressAnimation ? ' no-animate' : ''}`}>
                {isCurrent && (
                    <button
                        onClick={() => navigate('/')}
                        className="btn btn-link text-decoration-none text-primary position-absolute z-3 back-button"
                    >
                        <span className="fas fa-chevron-left me-2"/>
                        Back
                    </button>
                )}

                <div className="highlight-film">
                    <div
                        className="film-cover d-flex flex-column align-items-end"
                        style={{backgroundImage: `url("${imgUrl(m.still?.imageUrl) || defaultCover}")`}}
                        title="Update still"
                    >
                        <div className="gradient w-100 mt-auto"></div>
                    </div>

                    {isCurrent && showArrows && (
                        <button
                            className="feed-nav-arrow feed-nav-left"
                            disabled={!hasPrev}
                            onClick={() => triggerSwipe('prev')}
                        >
                            <span className="fas fa-chevron-left" />
                        </button>
                    )}

                    {isCurrent && showArrows && (
                        <button
                            className="feed-nav-arrow feed-nav-right"
                            disabled={!hasNext}
                            onClick={() => triggerSwipe('next')}
                        >
                            <span className="fas fa-chevron-right" />
                        </button>
                    )}

                    <div className="film-actions ps-3">
                        <div className="film-logo position-relative" title="Movie logo">
                            {m.logo ? (
                                <img
                                    alt={m.title}
                                    src={imgUrl(m.logo.imageUrl)}
                                    className="img-fluid"/>
                            ) : (
                                <h3 className="py-4 mt-5 text-primary text-center text-uppercase fw-bold">
                                    {m.title}
                                </h3>
                            )}
                        </div>

                        <div className="icons pb-3 mt-3">
                            <span className="release-year">{m.releaseYear}</span>

                            <span className="restriction-genre-box ms-3">
                                {m.genres?.map(genre => (
                                    <Badge key={genre} color="transparent" className="text-uppercase">{genre}</Badge>
                                ))}
                            </span>

                            <span className="language-subtitles ms-3 text-capitalize">
                                {v.assetDetails?.audioLanguage?.length > 0 && (
                                    <Fragment>
                                        <span className="fas fa-language me-2"/>
                                        {(v.assetDetails && v.assetDetails?.audioLanguage.map(code => getLanguageByCode(code))) ||
                                            <span className="fas fa-minus"/>}
                                    </Fragment>
                                )}

                                {v.assetDetails?.subtitlesLanguage?.length > 0 && (
                                    <Fragment>
                                        <span className="fas fa-closed-captioning ms-3 me-2"/>
                                        {(v.assetDetails?.subtitlesLanguage.map(code => getLanguageByCode(code))) ||
                                            <span className="fas fa-minus"/>}
                                    </Fragment>
                                )}
                            </span>

                            {(() => {
                                const ad = v.assetDetails;
                                if (!ad) return null;
                                const parts = [
                                    ad.frameRate && (/^\d+$/.test(String(ad.frameRate)) ? `${ad.frameRate}fps` : ad.frameRate),
                                    ad.audioConfig,
                                    ad.resolution,
                                    ad.dimensions,
                                    ad.ratio,
                                    (ad.dbox === true || ad.dbox === 'true') && 'DBOX',
                                ].filter(Boolean);
                                if (parts.length === 0) return null;
                                return (
                                    <span className="asset-details ms-3 text-uppercase">
                                        <span className="fas fa-film me-2" />
                                        {parts.join(' · ')}
                                    </span>
                                );
                            })()}

                            {o?._id && (
                                <span className="price-credits ms-3">
                                    <span className="fas fa-coins me-2" />
                                    From {formatCredits(getSitePriceRange(v, organization).min)} to {formatCredits(getSitePriceRange(v, organization).max)} credits
                                </span>
                            )}
                        </div>

                        {isCurrent && ['site-admin', 'site-user'].includes(user.role) && (o && o.status === 'request-accepted') && (
                            <div className="border-start border-primary bg-broken my-3 p-3 animate-fade-in">
                                <p className="text-primary">Your request was accepted under the following conditions</p>
                                <strong>Screening Terms</strong>
                                <p>{o.screeningTerms}</p>

                                {o.bacCorrection !== 0 && (
                                    <Fragment>
                                        <div className="row">
                                            <p className="col text-nowrap">
                                                <span className="fas fa-calendar-check me-2 text-primary"/>
                                                <strong>Screening date: </strong> {screeningDateFmt}
                                            </p>
                                            <p className="col text-nowrap">
                                                <span className="fas fa-clock me-2 text-primary"/>
                                                {/* The agreed period, not the version default —
                                                    a BAC correction can change it. */}
                                                <strong>Hire period: </strong>{` ${purchase?.hirePeriod ?? o.version.defaultHirePeriod} ${purchase?.hirePeriodType ?? o.version.hirePeriodType}`}
                                            </p>
                                        </div>
                                        {o?.bacCorrection?.priceCorrection && (
                                            <div className="row my-2 w-50">
                                                <div className="col text-nowrap">
                                                    <span className="fas fa-coins me-2 text-primary "/>
                                                    <strong  >Price correction:</strong>
                                                </div>
                                                <div className="col">
                                                    {`${formatCredits(o?.bacCorrection?.priceCorrection)} credits`}
                                                </div>
                                            </div>
                                        )}
                                        <div className="row my-2 w-50">
                                            <div className='col text-nowrap' >
                                                <span className="fas fa-cash-register me-2 text-primary "/>
                                                <strong >Total:</strong>
                                            </div>
                                            <div className='col'>{`${formatCredits(Number(o?.downpayment) + Number(o?.bacCorrection?.priceCorrection))} credits`}</div>
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        )}

                        {isCurrent && o && <IngestProgress timezone={organization.timezone} />}

                        {isCurrent && (
                            <div className="d-flex gap-2 flex-wrap align-items-center my-3">
                                <ActionButton order={o} version={v} organization={organization} organizationContent={organizationContent} />
                                {t && t.length > 0 && t.map((trailerOrder, i) => (
                                    <PlayButton
                                        key={trailerOrder._id}
                                        order={trailerOrder}
                                        label={`Play Trailer ${i + 1}`}
                                        icon="fa-video"
                                        organization={organization}
                                        organizationContent={organizationContent}
                                    />
                                ))}
                                {m.youtubeTrailerId && (
                                    <button className="btn btn-outline-primary" onClick={toggleTrailer}>
                                        <span className="fab fa-youtube text-danger me-2"/>
                                        Watch Trailer
                                    </button>
                                )}
                            </div>
                        )}

                        {isCurrent && ['ready-on-mediablock', 'finished-playback'].includes(sw) && (
                            <HireCountdown order={o} />
                        )}

                        {isCurrent && playing && <PlayingBanner />}

                        {sw === 'request-declined' && dr && (
                            <div className="border-start border-danger mt-3 p-3 bg-broken">
                                <p className="fw-bold text-danger mb-1">Reason: </p> {dr}
                            </div>
                        )}

                        {sw !== 'request-declined' && purchase?.purchasedAt && (
                            <p className="active-orders small text-uppercase pt-3 mb-1 text-primary">
                                <span className="fas fa-shopping-cart me-2" />
                                Purchased at {new Date(purchase.purchasedAt).toLocaleString()}
                                {purchase.purchasedBy?.firstName && ` by ${purchase.purchasedBy.firstName} ${purchase.purchasedBy.lastName}`}
                            </p>
                        )}

                        <p className="movie-synopsis lh-2 pt-3">
                            {m.synopsis}
                        </p>
                    </div>
                </div>

                <div className="crew-cast-container p-3">
                    <h5 className="text-uppercase">Crew</h5>
                    <div className="crew-container d-flex flex-wrap">
                        {m.credits?.filter(({type}) => type === 'crew').map(credit => (
                            <CrewCard
                                key={credit._id}
                                type="crew"
                                credit={credit}
                                rolesList={rolesList}
                                showRole={true}
                            />
                        ))}
                    </div>

                    <h5 className="text-uppercase mt-3">Cast</h5>
                    <div className="cast-container d-flex flex-wrap">
                        {m.credits?.filter(({type}) => type === 'cast').map(credit => (
                            <CrewCard
                                key={credit._id}
                                type="cast"
                                credit={credit}
                                rolesList={rolesList}
                                showRole={false}
                            />
                        ))}
                    </div>
                </div>

                {/* Order Details Section — the latest purchase of this title */}
                {purchase && (
                    <div className="order-details-container p-3 border-top border-secondary animate-fade-in">
                        <div className="mb-4">
                            <h5 className="text-uppercase mb-3">
                                <span className="fas fa-receipt me-2 text-primary"/>
                                Order Information
                            </h5>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="order-info-item">
                                        <div className="text-white-50 small mb-1">Order Status</div>
                                        <div className="text-white fw-bold">
                                            <OrderStatusBadge status={o.statusWorkflow}  />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    {/* Reads the purchase, not the document: the
                                        document's screening date, hire period and
                                        price belong to whichever purchase last
                                        wrote them. */}
                                    <PurchaseTerms purchase={purchase} version={v} />
                                </div>

                                {o.version?.assetDetails && (
                                    <>
                                        <div className="col-md-6">
                                            <div className="order-info-item">
                                                <div className="text-white-50 small mb-1">Format</div>
                                                <div className="text-white">
                                                    <span className="badge bg-secondary me-1">{o.version.assetDetails.resolution}</span>
                                                    <span className="badge bg-secondary me-1">{o.version.assetDetails.dimensions}</span>
                                                    <span className="badge bg-secondary">{o.version.assetDetails.ratio}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="order-info-item">
                                                <div className="text-white-50 small mb-1">Audio</div>
                                                <div className="text-white">
                                                    <span className="badge bg-secondary me-1">{o.version.assetDetails.audioConfig}</span>
                                                    {o.version.assetDetails.audioLanguage?.length > 0 && (
                                                        <span className="badge bg-secondary">{o.version.assetDetails.audioLanguage.join(', ').toUpperCase()}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="col-md-6">
                                    <div className="order-info-item">
                                        <div className="text-white-50 small mb-1">Purchased</div>
                                        <div className="text-white" style={{fontSize: '0.85rem'}}>
                                            {purchase.purchasedAt ? new Date(purchase.purchasedAt).toLocaleString() : '—'}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="order-info-item">
                                        <div className="text-white-50 small mb-1">Last Updated</div>
                                        <div className="text-white" style={{fontSize: '0.85rem'}}>
                                            {new Date(o.updatedAt).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Activity on the latest purchase, driven by its own
                            events — the order's approvedAt/acceptedAt describe
                            whichever purchase last wrote them. */}
                        {purchase && (
                            <div className="activity-timeline">
                                <h5 className="text-uppercase mb-3">
                                    <span className="fas fa-history me-2 text-primary"/>
                                    Activity Timeline
                                </h5>

                                <PurchaseTimeline purchase={purchase} orderType={o.orderType} />
                            </div>
                        )}
                    </div>
                )}

                {/* Every earlier booking of this title by this cinema. The order
                    document is reused for all of them, so without this the page
                    can only ever describe the most recent one. */}
                {purchases.length > 1 && (
                    <div className="purchase-history-container p-3 border-top border-secondary animate-fade-in">
                        <h5 className="text-uppercase mb-3">
                            <span className="fas fa-clock-rotate-left me-2 text-primary"/>
                            Purchase History
                            <span className="text-white-50 ms-3 text-lowercase" style={{ fontSize: '0.85rem' }}>
                                {purchases.length} bookings of this title
                            </span>
                        </h5>
                        <PurchaseHistory purchases={purchases} isBAC={isAdmin(user)} status={o.statusWorkflow} />
                    </div>
                )}

                {/* Transaction Summary Section */}
                {o && o.purchasedAt && (o.downpaymentTransaction || o.bacCorrectionTransaction || o.revertTransaction) && (
                    <div className="transactions-container p-3 border-top border-secondary animate-fade-in">
                        <h5 className="text-uppercase mb-3">
                            <span className="fas fa-list-check me-2 text-primary"/>
                            Transaction History
                        </h5>

                        <div className="transactions-list d-flex flex-column gap-2">
                            {o.downpaymentTransaction && (
                                <div className={`transaction-item bg-broken px-3 py-3 border-start rounded d-flex justify-content-between align-items-center ${o.downpaymentTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                    <div className="flex-grow-1">
                                        <div className="text-uppercase fw-bold">
                                            <span className="fas fa-file-text me-2 text-primary"/>
                                            {o.downpaymentTransaction.description}
                                        </div>
                                        <small className="text-white-50 d-block mt-1">
                                            {new Date(o.downpaymentTransaction.effectiveDate).toLocaleString()}
                                        </small>
                                    </div>
                                    <div className="text-end ms-3">
                                        <div className="text-white-50 small mb-1">Balance</div>
                                        <div className="text-white small">
                                            {formatCredits(o.downpaymentTransaction.balanceBeforeTransaction)}
                                            <span className="text-primary mx-2">&rarr;</span>
                                            {formatCredits(o.downpaymentTransaction.balanceAfterTransaction)}
                                        </div>
                                        <div className={`lead fw-bold mt-2 ${o.downpaymentTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'}`}>
                                            <span className={`fas me-1 ${o.downpaymentTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`}/>
                                            {formatCredits(o.downpaymentTransaction.amount)}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {o.bacCorrectionTransaction && (
                                <div className={`transaction-item bg-broken px-3 py-3 border-start rounded d-flex justify-content-between align-items-center ${o.bacCorrectionTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                    <div className="flex-grow-1">
                                        <div className="text-uppercase fw-bold">
                                            <span className="fas fa-file-text me-2 text-primary"/>
                                            {o.bacCorrectionTransaction.description}
                                        </div>
                                        <small className="text-white-50 d-block mt-1">
                                            {new Date(o.bacCorrectionTransaction.effectiveDate).toLocaleString()}
                                        </small>
                                    </div>
                                    <div className="text-end ms-3">
                                        <div className="text-white-50 small mb-1">Balance</div>
                                        <div className="text-white small">
                                            {formatCredits(o.bacCorrectionTransaction.balanceBeforeTransaction)}
                                            <span className="text-primary mx-2">&rarr;</span>
                                            {formatCredits(o.bacCorrectionTransaction.balanceAfterTransaction)}
                                        </div>
                                        <div className={`lead fw-bold mt-2 ${o.bacCorrectionTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'}`}>
                                            <span className={`fas me-1 ${o.bacCorrectionTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`}/>
                                            {formatCredits(o.bacCorrectionTransaction.amount)}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {o.revertTransaction && (
                                <div className={`transaction-item bg-broken px-3 py-3 border-start rounded d-flex justify-content-between align-items-center ${o.revertTransaction.transactionType === 'credit' ? 'border-success' : 'border-danger'}`}>
                                    <div className="flex-grow-1">
                                        <div className="text-uppercase fw-bold">
                                            <span className="fas fa-file-text me-2 text-primary"/>
                                            {o.revertTransaction.description}
                                        </div>
                                        <small className="text-white-50 d-block mt-1">
                                            {new Date(o.revertTransaction.effectiveDate).toLocaleString()}
                                        </small>
                                    </div>
                                    <div className="text-end ms-3">
                                        <div className="text-white-50 small mb-1">Balance</div>
                                        <div className="text-white small">
                                            {formatCredits(o.revertTransaction.balanceBeforeTransaction)}
                                            <span className="text-primary mx-2">&rarr;</span>
                                            {formatCredits(o.revertTransaction.balanceAfterTransaction)}
                                        </div>
                                        <div className={`lead fw-bold mt-2 ${o.revertTransaction.transactionType === 'credit' ? 'text-success' : 'text-danger'}`}>
                                            <span className={`fas me-1 ${o.revertTransaction.transactionType === 'credit' ? 'fa-plus' : 'fa-minus'}`}/>
                                            {formatCredits(o.revertTransaction.amount)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="clearfix"/>

                {isCurrent && (
                    <Modal isOpen={modalTrailer} toggle={toggleTrailer} fullscreen modalClassName="trailer">
                        <ModalHeader tag="div" toggle={toggleTrailer} className="non-editable" />
                        <ModalBody>
                            <YoutubeTrailer
                                videoId={m.youtubeTrailerId}
                                title={m.title}
                                value={m.youtubeTrailerId}
                                fullscreen={true}
                            />
                        </ModalBody>
                    </Modal>
                )}
            </div>
        );
    };

    return movieVersionStatus === 'success' && user && (
        <div className="swipe-container">
            <div
                className="swipe-panels"
                ref={panelsRef}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                {prevItem && (
                    <div className="swipe-panel swipe-panel-prev">
                        {(() => {
                            const cached = dataCache.current.get(prevItem._id);
                            return cached
                                ? renderMovieDetail({ movie: cached.movie, version: cached.version, order: cached.order, trailers: cached.trailers }, false)
                                : <MoviePreviewPanel data={cached} feedItem={prevItem} />;
                        })()}
                    </div>
                )}

                <div className="swipe-panel swipe-panel-current">
                    {renderMovieDetail({ movie, version, order: currentOrder, trailers }, true)}
                </div>

                {nextItem && (
                    <div className="swipe-panel swipe-panel-next">
                        {(() => {
                            const cached = dataCache.current.get(nextItem._id);
                            return cached
                                ? renderMovieDetail({ movie: cached.movie, version: cached.version, order: cached.order, trailers: cached.trailers }, false)
                                : <MoviePreviewPanel data={cached} feedItem={nextItem} />;
                        })()}
                    </div>
                )}
            </div>
        </div>
    )
}

export default MoviePageBAC;
